import React from "react";

/**
 * Global ErrorBoundary — replaces the raw white/black screen with a friendly
 * "something went wrong" page and a link to /status when React throws during
 * render. Uses window.location because we can't rely on react-router state
 * once the tree has unmounted.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[Copilot ErrorBoundary]", error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || "Unknown error");
    return (
      <div className="min-h-screen bg-[#0c0a09] text-stone-100 flex items-center justify-center px-4" data-testid="error-boundary">
        <div className="w-full max-w-lg border border-stone-800 bg-[#1c1917] p-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-500/80">Something broke</div>
          <h1 className="font-serif text-3xl mt-3 text-stone-50 tracking-tight">
            Copilot ran into a snag.
          </h1>
          <p className="mt-3 text-sm text-stone-400 leading-relaxed">
            The interface hit an unexpected error and stopped rendering. Nothing on the server side has been lost —
            your data, memory and pending approvals are safe. Try reloading, or open the status page to see if the API is healthy.
          </p>
          <pre className="mt-4 text-[11px] font-mono text-stone-500 border border-stone-800 p-3 whitespace-pre-wrap max-h-32 overflow-auto" data-testid="error-detail">
            {msg}
          </pre>
          <div className="mt-6 flex items-center gap-3">
            <button
              data-testid="error-reload"
              onClick={() => window.location.reload()}
              className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white transition-colors"
            >
              Reload
            </button>
            <a
              data-testid="error-status-link"
              href="/status"
              className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 border border-stone-700 hover:border-stone-500 text-stone-300 transition-colors"
            >
              Open status page
            </a>
          </div>
        </div>
      </div>
    );
  }
}
